import { Alert, Button, Form, Input, Modal, Radio, Select, Space, Tag, Upload } from "antd";
import { ExclamationCircleOutlined, PaperClipOutlined, SaveOutlined, SendOutlined, TeamOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import RecipientBrowserModal from "./recipientBrowserModal";
import { create_message } from "../../api/messages";
import { createFileAttachement } from "../../api/fileAttachement";
import { Action, buildPermission, buildPermissionContext, Resource } from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";

export enum Target_type {
  etudiant = "STUDENT",
  "tous les étudiants" = "ALL_STUDENTS",
  "étudiants et parents" = "STUDENT_AND_PARENTS",
  "tous les étudiants et parents" = "ALL_STUDENTS_PARENTS",
  parent = "PARENTS",
  "tous les parents" = "ALL_PARENTS",
  enseignant = "TEACHER",
  "tous les enseignants" = "ALL_TEACHERS",
  personnel = "STAFF",
  "tout le personnel" = "ALL_STAFF",
  "étudiants de la classe" = "CLASS_STUDENTS",
  "toute l'université" = "ALL_UNIVERSITY",
  "sélection personnalisée" = "CUSTOM",
}

export enum Message_type { INFO = "Info", URGENT = "Urgent", WARNING = "Avertissement" }

const emptyRecipients = { TeacherIds: [], StudentIds: [], ParentIds: [], StaffIds: [], ClassIds: [] };
const schema = z.object({
  recipients: z.object({
    TeacherIds: z.array(z.string()), StudentIds: z.array(z.string()), ParentIds: z.array(z.string()),
    StaffIds: z.array(z.string()), ClassIds: z.array(z.string()),
  }),
  target_type: z.nativeEnum(Target_type),
  message_type: z.nativeEnum(Message_type),
  title: z.string().trim().min(1, "Veuillez saisir un objet"),
  message: z.string().trim().min(1, "Le message ne peut pas être vide"),
  files: z.array(z.any()).optional(),
});
type FormData = z.infer<typeof schema>;
type SendMode = "send" | "draft";

const groupOptions = [
  { label: "Tous les étudiants", value: Target_type["tous les étudiants"] },
  { label: "Tous les parents", value: Target_type["tous les parents"] },
  { label: "Tous les enseignants", value: Target_type["tous les enseignants"] },
  { label: "Tout le personnel", value: Target_type["tout le personnel"] },
  { label: "Tous les étudiants et leurs parents", value: Target_type["tous les étudiants et parents"] },
  { label: "Toute l’université", value: Target_type["toute l'université"] },
];

const ModalMessage = ({ visible, onCancel }: { visible: boolean; onCancel: () => void }) => {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [audienceMode, setAudienceMode] = useState<"custom" | "group">("custom");
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit, reset, setValue, getValues, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      recipients: emptyRecipients, target_type: Target_type["sélection personnalisée"],
      message_type: Message_type.INFO, title: "", message: "", files: [],
    },
  });
  const recipients = watch("recipients");
  const targetType = watch("target_type");
  const files = watch("files") || [];
  const selectedCount = useMemo(
    () => Object.values(recipients).reduce((total, ids) => total + ids.length, 0), [recipients],
  );

  const closeAndReset = () => { reset(); setAudienceMode("custom"); onCancel(); };
  const submit = async (data: FormData, mode: SendMode) => {
    if (audienceMode === "custom" && selectedCount === 0) {
      toast.error("Ajoutez au moins un destinataire"); setBrowserOpen(true); return;
    }
    const doSubmit = async () => {
      setSubmitting(true);
      try {
        const response = await create_message({
          target: data.target_type, title: data.title, message: data.message, type: data.message_type,
          studentId: data.recipients.StudentIds, classId: data.recipients.ClassIds,
          parentId: data.recipients.ParentIds, teacherId: data.recipients.TeacherIds,
          staffId: data.recipients.StaffIds, message_sent: mode === "send",
          draft: mode === "draft", archive: false,
        }, buildPermissionContext(Resource.MESSAGE, Action.CREATE));
        if (files.length) {
          const messageId = response?.data?.id || response?.messageId || response?.newMessage?.id;
          if (!messageId) throw new Error("Identifiant du message introuvable");
          const formData = new FormData();
          files.forEach((file: any) => formData.append("files", file));
          formData.append("entity_id", messageId); formData.append("entity_type", "Message");
          await createFileAttachement(formData, buildPermissionContext(Resource.MESSAGE, Action.CREATE));
        }
        toast.success(mode === "send" ? "Message envoyé avec succès" : "Brouillon enregistré");
        closeAndReset();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error?.response?.data?.error || error?.message || "Une erreur est survenue");
      } finally { setSubmitting(false); }
    };
    if (mode === "send" && targetType === Target_type["toute l'université"]) {
      Modal.confirm({
        title: "Envoyer à toute l’université ?", icon: <ExclamationCircleOutlined />,
        content: "Tous les étudiants, parents, enseignants et membres du personnel recevront ce message.",
        okText: "Confirmer l’envoi", cancelText: "Annuler", okButtonProps: { danger: true }, onOk: doSubmit,
      });
    } else await doSubmit();
  };
  const runSubmit = (mode: SendMode) => handleSubmit((data) => submit(data, mode))();

  return (
    <Modal title="Nouveau message" open={visible} onCancel={closeAndReset} width={760} destroyOnClose
      footer={<Space>
        <Button onClick={closeAndReset} disabled={submitting}>Annuler</Button>
        <Button icon={<SaveOutlined />} onClick={() => runSubmit("draft")} loading={submitting}>Enregistrer comme brouillon</Button>
        <Button type="primary" icon={<SendOutlined />} onClick={() => runSubmit("send")} loading={submitting}>
          {audienceMode === "custom" && selectedCount ? `Envoyer à ${selectedCount} sélection${selectedCount > 1 ? "s" : ""}` : "Envoyer le message"}
        </Button>
      </Space>}
    >
      <Form layout="vertical">
        <Form.Item label="Destinataires" required>
          <Radio.Group optionType="button" buttonStyle="solid" value={audienceMode}
            onChange={(event) => {
              const mode = event.target.value; setAudienceMode(mode);
              if (mode === "custom") setValue("target_type", Target_type["sélection personnalisée"]);
              else { setValue("recipients", emptyRecipients); setValue("target_type", Target_type["tous les étudiants"]); }
            }}
            options={[{ label: "Personnes ou classes", value: "custom" }, { label: "Groupe entier", value: "group" }]}
          />
        </Form.Item>
        {audienceMode === "group" ? (
          <Controller name="target_type" control={control} render={({ field }) =>
            <Form.Item label="Groupe à contacter" required><Select {...field} options={groupOptions} size="large" /></Form.Item>} />
        ) : (
          <div style={{ marginBottom: 20 }}>
            <Button type="dashed" size="large" block icon={<TeamOutlined />} onClick={() => setBrowserOpen(true)}>
              {selectedCount ? "Modifier les destinataires" : "Ajouter des destinataires"}
            </Button>
            {!!selectedCount && <div style={{ marginTop: 10, padding: 12, background: "#f6f8fb", borderRadius: 8 }}>
              <strong>{selectedCount} sélection{selectedCount > 1 ? "s" : ""}</strong><div style={{ marginTop: 8 }}>
                {!!recipients.ClassIds.length && <Tag color="cyan">{recipients.ClassIds.length} classe(s)</Tag>}
                {!!recipients.StudentIds.length && <Tag color="blue">{recipients.StudentIds.length} étudiant(s)</Tag>}
                {!!recipients.TeacherIds.length && <Tag color="green">{recipients.TeacherIds.length} enseignant(s)</Tag>}
                {!!recipients.ParentIds.length && <Tag color="orange">{recipients.ParentIds.length} parent(s)</Tag>}
                {!!recipients.StaffIds.length && <Tag color="purple">{recipients.StaffIds.length} personnel</Tag>}
              </div><small style={{ color: "#667085" }}>Une classe compte comme une sélection ; ses étudiants seront ajoutés lors de l’envoi.</small>
            </div>}
          </div>
        )}
        <Controller name="message_type" control={control} render={({ field }) =>
          <Form.Item label="Priorité"><Radio.Group {...field} optionType="button" buttonStyle="solid">
            <Radio.Button value={Message_type.INFO}>Normale</Radio.Button>
            <Radio.Button value={Message_type.WARNING}>Importante</Radio.Button>
            <Radio.Button value={Message_type.URGENT}>Urgente</Radio.Button>
          </Radio.Group></Form.Item>} />
        <Controller name="title" control={control} render={({ field }) =>
          <Form.Item label="Objet" required validateStatus={errors.title ? "error" : ""} help={errors.title?.message}>
            <Input {...field} size="large" placeholder="Objet du message" maxLength={150} showCount />
          </Form.Item>} />
        <Controller name="message" control={control} render={({ field }) =>
          <Form.Item label="Message" required validateStatus={errors.message ? "error" : ""} help={errors.message?.message}>
            <Input.TextArea {...field} placeholder="Écrivez votre message…" autoSize={{ minRows: 8, maxRows: 16 }} />
          </Form.Item>} />
        <Controller name="files" control={control} render={() => <Upload.Dragger multiple fileList={files}
          beforeUpload={(file) => { setValue("files", [...(getValues("files") || []), file]); return false; }}
          onRemove={(file) => setValue("files", (getValues("files") || []).filter((item: any) => item.uid !== file.uid))}>
          <PaperClipOutlined style={{ fontSize: 22 }} /><div>Glissez vos pièces jointes ici ou cliquez pour les sélectionner</div>
        </Upload.Dragger>} />
        {targetType === Target_type["toute l'université"] && <Alert style={{ marginTop: 16 }} type="warning" showIcon
          message="Envoi général" description="Une confirmation sera demandée avant l’envoi à toute l’université." />}
      </Form>
      <RecipientBrowserModal visible={browserOpen} onCancel={() => setBrowserOpen(false)} recipients={recipients}
        setRecipients={(value: any) => setValue("recipients", value, { shouldValidate: true })}
        onConfirm={() => { setValue("target_type", Target_type["sélection personnalisée"]); setBrowserOpen(false); }} />
    </Modal>
  );
};

export default withPermission(ModalMessage, [buildPermission(Resource.MESSAGE, Action.CREATE)], "masquer");
